import * as R from 'ramda';
import { Role, type BookmarkParentType } from '~/apollo/generated/v3/graphql';
import { RoleSecured } from '~/components/auth/RoleSecured';
import type { TargetBookmarksManagerModalProps } from '~/components/bookmark/TargetBookmarksManagerModal';
import type { SupportingObject } from '~/components/supporting-object/so-item-display-v4';
import { SOItemDisplayV4 } from '~/components/supporting-object/so-item-display-v4';
import { useShowUnpublished } from '~/components/upload/supporting-object/use-show-unpublished';
import { cn } from '~/utils/common';
import type { RefetchQueries } from '~/utils/graphql';
import {
  countUnpublishedItems,
  isPublished,
} from '~/utils/modules/supporting-object';

export type BookmarkableSOProps = Pick<
  TargetBookmarksManagerModalProps,
  'parentType' | 'path'
>;

export function SOItemListV4({
  items: unsortedItems,
  fade = false,
  soParentType,
  soParentId,
  bookmarkParentType,
  refetchQueries,
}: {
  items: SupportingObject[];
  fade?: boolean;
  soParentType: 'outcrop' | 'study';
  soParentId: number;
  bookmarkParentType?: BookmarkParentType;
  refetchQueries: RefetchQueries;
}) {
  const unpublishedCount =
    countUnpublishedItems(unsortedItems) +
    countUnpublishedItems(unsortedItems.flatMap(item => item.pictures));

  const { showUnpublished, PublishedToggle } =
    useShowUnpublished(unpublishedCount);

  const items = unsortedItems
    .filter(item => showUnpublished || isPublished(item))
    .sort(R.ascend(item => item.priority ?? Infinity));

  return (
    <div className="space-y-6">
      <RoleSecured roles={[Role.RoleAdmin]}>
        <PublishedToggle />
      </RoleSecured>

      <div
        className={cn('grid gap-6', {
          'lg:grid-cols-1': items.length < 2,
          'lg:grid-cols-2': items.length === 2,
          'lg:grid-cols-3': items.length === 3,
          'lg:grid-cols-4': items.length > 3,
        })}
      >
        {items.map(so => (
          <SOItemDisplayV4
            key={so.id}
            item={so}
            fade={fade}
            soParentType={soParentType}
            soParentId={soParentId}
            bookmarkParentType={bookmarkParentType}
            bookmarkParentId={parseInt(so.id)}
            showUnpublished={showUnpublished}
            refetchQueries={refetchQueries}
          />
        ))}
      </div>
    </div>
  );
}
